import { ReactElement, PropsWithChildren } from "react";
import classNames from "classnames";
import { ButtonBase } from "client/Components";
import { setTrackingId } from "client/Misc/utils";
import { useCallbackWithoutArguments } from "client/Hooks";
import styles from "./ListItem.module.less";

const ListItemContent = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.content}>
			{ children }
		</div>
	);
};
ListItemContent.displayName = "ListItem.Content";

export type ListItemRowProps = {
	verticalAlignment?: "default" | "center";
};

const ListItemRow = (props: PropsWithChildren<ListItemRowProps>): ReactElement => {
	const {
		verticalAlignment = "default",
		children
	} = props;

	return (
		<div
			className={classNames(
				styles.row,
				{ [styles.row_verticalCenter]: verticalAlignment === "center" }
			)}
		>
			{ children }
		</div>
	);
};
ListItemRow.displayName = "ListItem.Row";

export type ListItemTitleProps = {
	trackingId?: string;
	isStrikedThrough?: boolean;
};

const ListItemTitle = (props: PropsWithChildren<ListItemTitleProps>): ReactElement => {
	const {
		trackingId,
		isStrikedThrough = false,
		children
	} = props;

	return (
		<h4
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.title,
				{ [styles.title_strikeThrough]: isStrikedThrough }
			)}
		>
			{ children }
		</h4>
	);
};
ListItemTitle.displayName = "ListItem.Title";

const ListItemDescription = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.description}>
			{ children }
		</div>
	);
};
ListItemDescription.displayName = "ListItem.Description";

export type ListItemRightAdditionProps = {
	verticalAlignment?: "default" | "center";
};

const ListItemRightAddition = (props: PropsWithChildren<ListItemRightAdditionProps>): ReactElement => {
	const {
		verticalAlignment = "default",
		children
	} = props;

	return (
		<div
			className={classNames(
				styles.rightAddition,
				{ [styles.rightAddition_verticalCenter]: verticalAlignment === "center" }
			)}
		>
			{ children }
		</div>
	);
};
ListItemRightAddition.displayName = "ListItem.RightAddition";

export type ListItemInfoProps = {
	width?: "fill" | "auto";
	trackingId?: string;
};

const ListItemInfo = (props: PropsWithChildren<ListItemInfoProps>): ReactElement => {
	const {
		children,
		width = "auto",
		trackingId
	} = props;

	return (
		<div
			data-tracking-id={trackingId}
			className={
				classNames(
					styles.info,
					{
						[styles.info_fillWidth]: width === "fill"
					}
				)
			}
		>
			{ children }
		</div>
	);
};
ListItemInfo.displayName = "ListItem.Info";

const ListItemIcon = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.icon}>
			{ children }
		</div>
	);
};
ListItemIcon.displayName = "ListItem.Icon";

export type ClickableAreaPosition = "first" | "last";

const clickableAreaPositionClasses: Record<ClickableAreaPosition, string> = {
	first: styles.clickableArea_first,
	last: styles.clickableArea_last
};

export type ListItemClickableAreaProps = {
	position?: ClickableAreaPosition | undefined | readonly ClickableAreaPosition[];
	shouldGrow?: boolean;
	shouldOnlyBeVisibleOnHover?: boolean;
	onClick(): void;
	onMouseEnter?(): void;
	onMouseLeave?(): void;
	isDisabled?: boolean;
	tooltip?: string;
	trackingId?: string;
};

const ListItemClickableArea = (props: PropsWithChildren<ListItemClickableAreaProps>): ReactElement => {
	const {
		position = [],
		shouldGrow,
		shouldOnlyBeVisibleOnHover,
		onClick,
		onMouseEnter,
		onMouseLeave,
		isDisabled = false,
		tooltip,
		trackingId,
		children
	} = props;

	let normalizedPosition: ClickableAreaPosition | readonly ClickableAreaPosition[] = position;
	if (typeof normalizedPosition === "string") {
		normalizedPosition = [normalizedPosition];
	}

	const mouseEnterHandler: (() => void) | undefined = useCallbackWithoutArguments(onMouseEnter);
	const mouseLeaveHandler: (() => void) | undefined = useCallbackWithoutArguments(onMouseLeave);

	return (
		<ButtonBase
			className={classNames(
				styles.clickableArea,
				{
					[styles.clickableArea_shouldGrow]: shouldGrow,
					[styles.clickableArea_onlyVisibleOnHover]: shouldOnlyBeVisibleOnHover
				},
				normalizedPosition.map(x => clickableAreaPositionClasses[x])
			)}
			onClick={onClick}
			trackingId={trackingId}
			disabled={isDisabled}
			title={tooltip}
			onMouseEnter={mouseEnterHandler}
			onMouseLeave={mouseLeaveHandler}
		>
			{ children }
		</ButtonBase>
	);
};
ListItemClickableArea.displayName = "ListItem.ClickableArea";

export type ListItemStyle =
	| "success"
	| "warning"
	| "danger"
	| "secondary";

const listItemStyleClasses: Record<ListItemStyle, string | undefined> = {
	success: styles.root_success,
	warning: styles.root_warning,
	danger: styles.root_danger,
	secondary: undefined
};

export type ListItemProps = {
	title?: string;
	style?: ListItemStyle;
	blockClassName?: string;
	onClick?(): void;
	isHoverable?: boolean;
	isWide?: boolean;
	hasSeparateClickableAreas?: boolean;
	trackingId?: string;
	hasStatusDot?: boolean;
};

export const ListItem = (props: PropsWithChildren<ListItemProps>): ReactElement => {
	const {
		title,
		style,
		blockClassName,
		isHoverable = false,
		isWide = false,
		hasSeparateClickableAreas = false,
		onClick,
		children,
		trackingId,
		hasStatusDot = false
	} = props;

	return (
		<li
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				blockClassName,
				style && listItemStyleClasses[style],
				{
					[styles.root_hoverable]: isHoverable,
					[styles.root_wide]: isWide,
					[styles.root_hasSeparateClickableAreas]: hasSeparateClickableAreas
				})
			}
			title={title}
			onClick={onClick}
		>
			{hasStatusDot && (
				<div
					{...setTrackingId("status-dot")}
					className={styles.statusDot}
				/>
			)}
			{ children }
		</li>
	);
};
ListItem.displayName = "ListItem";

ListItem.Content = ListItemContent;
ListItem.Row = ListItemRow;
ListItem.Title = ListItemTitle;
ListItem.Description = ListItemDescription;
ListItem.RightAddition = ListItemRightAddition;
ListItem.Info = ListItemInfo;
ListItem.Icon = ListItemIcon;
ListItem.ClickableArea = ListItemClickableArea;